import dayjs, { Dayjs } from 'dayjs';
import qs from 'qs';
import React, { createContext, Dispatch, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import LocalizedStringsFactory from 'react-localization';
import { useNavigate } from 'react-router-dom';
import store from 'store2';
import {
    getAdditionalDealers,
    getDefaultServiceAdvisor,
    getKiosk,
    getKioskBannerImages,
    getServiceOpenTimeRanges,
    IHybridTimeTable,
    insertAuditTrail,
} from '../../API/GoMoto';
import { Country, DealerStatus, DMSType, Events, KioskTypeName, LoanerOrRentalOption, PaymentProvider, Timezone, ValuationScreen } from '../../API/Enums';
import { images } from '../../assets/img';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { AppointmentDispatchTypes, useAppointmentDispatch, useAppointmentStateUnsafe } from '../Appointment/appointment';
import { useCardReader, useHardwareProviderState } from '../hardware';
import { useClearLocale, useLocale, useLocalization, useLocalizationDispatch } from '../Localization/localization';
import { useMZeroMachineId } from '../mzero';
import { useSavedSessionFinish } from '../savedSession';
import { Spinner } from '../../controls/Spinner/Spinner';
import { GMDialog } from '../../controls/Dialog';
import {
    GenerateDealerLocalizationFunction,
    GenerateKioskLocalizationFunction,
    KioskActions,
    KioskDispatchTypes,
    KioskImageType,
    KioskProviderProps,
    KioskReducerFunction,
    KioskRetrievalParams,
    KioskContextState,
    UseAdditionalDealersFunction,
    UseArePaymentsEnabledFunction,
    UseDefaultServiceAdvisorFunction,
    UseFullResetFunction,
    UseGetKioskRetrievalParamsFunction,
    KioskTimeTableHook,
    UseKioskBannerImagesFunction,
    UseKioskDispatchFunction,
    UseKioskFullStateFunction,
    UseKioskImageFunction,
    UseKioskLocalizationFunction,
    UseKioskStateFunction,
    UseMonochromeThemeFunction,
    UseNoAnimationsFunction,
    UsePossibleDealersFunction,
    UseSwitchDealerFunction,
    UseTowInSupportFunction,
    KioskOpenHours,
    Dealer,
    Kiosk,
} from './kiosk.types';
import { GMButton } from '../../controls/Button';
import { KioskThemeProvider } from '../KioskTheme/kioskTheme';
import { Locales, LocalizationDispatchTypes } from '../Localization/localization.types';
import { isDevelopment } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { GoMotoFunction } from '../../API/GoMoto.types';
import { useDevelopmentDispatch, useDevelopmentState } from '../Development/Development';
import { DevelopmentDispatchTypes } from '../Development/Development.types';

const initialState: KioskContextState = {
    kiosk: {
        key: '',
        kioskType: 'express',
        kioskTypeName: KioskTypeName.TheExpress,
        isMultiMake: false,
        keyStorageType: 'locker',
        name: '',
        timeout: 0,
        machineId: '',
        templateFolder: '',
        localization: new LocalizedStringsFactory({
            [Locales.en]: {
                disclaimer: '',
                hybridErrorMessage: '',
                otherKioskErrorMessage: '',
                location: '',
            },
        }),
        keyPickupOption: true,
    },
    dealer: {
        advisorOptionEnabled: false,
        alternateLanguage: Locales.en,
        blankInitialMileage: false,
        city: '',
        consentToSignElectronicallyEnabled: false,
        country: Country.USA,
        customerTextingEnabled: false,
        customerTextingActive: false,
        customUpsellName: '',
        customUpsellOpCode: '',
        customUpsellPageEnabled: false,
        defaultCommentOpCode: '',
        dmsType: DMSType.REYNOLDS,
        exclusiveValuationPage: false,
        facilityId: '',
        hunterAlignmentEnabled: false,
        hunterAlignmentOpCode: '',
        hunterTireTreadEnabled: false,
        hunterTireTreadOpCode: '',
        recallType: 'none',
        recallsActive: false,
        includeCurrentYear: false,
        key: '',
        maintenanceMenusEnabled: false,
        make: '',
        mobileCheckInEnabled: false,
        miscellaneousEnabled: false,
        name: '',
        newAppointmentEnabled: false,
        newCustomerEnabled: false,
        oldAppointmentEnabled: false,
        onlinePaymentsEnabled: false,
        onlinePaymentsActive: false,
        paymentAccount: '',
        paymentAccountStatus: null,
        paymentProvider: PaymentProvider.None,
        phone: '',
        postalCode: '',
        preliminaryEstimateEnabled: false,
        primaryLanguage: Locales.en,
        recommendedNotDoneEnabled: false,
        retainOldPartsEnabled: false,
        roBackerAcknowledgmentRequired: false,
        roBackerEnabled: false,
        roBackerImage: undefined,
        roBackerImageAltLang: undefined,
        roBackerReviewRequired: false,
        safeHarborWarningEnabled: false,
        serviceAdvisorEnabled: false,
        serviceCommentsEnabled: false,
        serviceEditingEnabled: false,
        showDmsServiceLineEstimates: false,
        showXtimeSingleServicePrices: false,
        stateEstimateEnabled: false,
        stateEstimateMinimum: 0,
        stateOrProvince: '',
        straightToServicesForAppointments: false,
        street: '',
        surveyEnabled: false,
        tagNumberEnabled: false,
        tagNumberRequired: false,
        theme: '',
        themeBannerEnabled: false,
        timezone: 4,
        transportationPageEnabled: false,
        valuationFullScreen: false,
        valuationFullScreenImage: '',
        valuationScreen: ValuationScreen.None,
        mobileValuationScreen: undefined,
        blackbookAdjustmentMethod: undefined,
        blackbookAdjustment: undefined,
        blackbookMinimumValuation: undefined,
        valuationPageEnabled: false,
        vehiclePickupEnabled: false,
        vehiclePickupInstructionsOpCode: '',
        vehicleRuleSetting: '',
        vinEnabled: false,
        vinRequired: false,
        vehicleTowInOption: false,
        website: '',
        workAuthorizationEnabled: false,
        xtimeEnabled: false,
        localization: new LocalizedStringsFactory({
            [Locales.en]: {
                vehiclePickupExtraText: '',
                disclaimerExtra: '',
                echoPageReminderText: '',
                customUpsellPageText: '',
                upsellDisclaimer: '',
                arrivalInstructions: '',
                fullKioskInstructions: '',
                consentToSignMessage: '',
                declineConsentToSignMessage: '',
                workAuthorizationDisclaimer: '',
                retainOldPartsAdditionalText: '',
                miscellaneousDisclaimer: '',
            },
        }),
        loanerOrRentalOption: LoanerOrRentalOption.None,
        status: DealerStatus.Active,
        hasExpressOr365: false,
        hasPhysicalKiosk: false,
    },
    hours: [],
    additionalDealers: [],
    possibleDealers: undefined,
    bannerImages: [],
    defaultServiceAdvisor: undefined,
    monochromeTheme: false,
    noAnimations: false,
    locale: Locales.en,
};

const kioskReducer: KioskReducerFunction = (state: KioskContextState, action: KioskActions) => {
    switch (action.type) {
        case KioskDispatchTypes.Initialize:
            return {
                ...state,
                ...action.payload,
            };
        case KioskDispatchTypes.RefetchKiosk:
            return { ...initialState };
        case KioskDispatchTypes.SetLocale:
            if (state.locale !== action.payload) {
                state.kiosk.localization.setLanguage(action.payload);
                state.dealer.localization.setLanguage(action.payload);

                return {
                    ...state,
                    locale: action.payload,
                };
            }
            else {
                return state;
            }
        case KioskDispatchTypes.SetDealer:
            if (state.dealer.key !== action.payload.key) {
                const localization = generateDealerLocalization(action.payload);
                localization.setLanguage(state.locale);

                return {
                    ...state,
                    dealer: {
                        ...action.payload,
                        localization,
                    },
                };
            }

            return state;
        case KioskDispatchTypes.SetDefaultServiceAdvisor:
            return {
                ...state,
                defaultServiceAdvisor: action.payload,
            };
        default:
            return state;
        case KioskDispatchTypes.SetPossibleDealers:
            return {
                ...state,
                possibleDealers: action.payload,
            };
        case KioskDispatchTypes.ClearPossibleDealers:
            return {
                ...state,
                possibleDealers: undefined,
            };
        case KioskDispatchTypes.SetMonochromeTheme:
            store.set('monochromeTheme', true);

            return {
                ...state,
                monochromeTheme: true,
            };
        case KioskDispatchTypes.ClearMonochromeTheme:
            store.set('monochromeTheme', false);

            return {
                ...state,
                monochromeTheme: false,
            };
        case KioskDispatchTypes.SetNoAnimations:
            store.set('noAnimations', true);

            return {
                ...state,
                noAnimations: true,
            };
        case KioskDispatchTypes.ClearNoAnimations:
            store.set('noAnimations', false);

            return {
                ...state,
                noAnimations: false,
            };
    }
};

export const KioskStateContext = createContext<KioskContextState | undefined>(undefined);
export const KioskDispatchContext = createContext<Dispatch<KioskActions> | undefined>(undefined);

export const KioskProvider: React.FC<KioskProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(kioskReducer, initialState);
    const [initFailureCount, setInitFailureCount] = useState<number>(0);
    const locale = useLocale();
    const localizationDispatch = useLocalizationDispatch();
    const Localization = useLocalization();
    const { kioskKey, machineId, subdomain } = useGetKioskRetrievalParams();
    const { isDev, kioskKey: devKioskKey } = useDevelopmentState();
    const developmentDispatch = useDevelopmentDispatch();

    useEffect(() => {
        if ((isDev && devKioskKey) || ((kioskKey || machineId || subdomain) && !state.kiosk.key && initFailureCount < 10)) {
            console.log('KioskProvider|loading kiosk using params: ', {
                kioskKey,
                machineId,
                subdomain,
            });

            getKiosk({
                kioskKey: isDev && devKioskKey ? devKioskKey : kioskKey,
                machineId,
                subdomain,
            })
                .then((kiosk: Kiosk) => {
                    if (kiosk) {
                        console.log('KioskProvider|kiosk found: ', kiosk.key);
                        store.session.set('kiosk', kiosk.key);

                        Promise.all([
                            getServiceOpenTimeRanges(kiosk.key),
                            (isElectron() && getKioskBannerImages(kiosk.key)) || Promise.resolve([]),
                            getAdditionalDealers(kiosk.key),
                            getDefaultServiceAdvisor(kiosk.key),
                        ])
                            .then(([hours, bannerImages, additionalDealers, defaultServiceAdvisor]) => {
                                console.log('KioskProvider|loaded dealer', kiosk.dealer.name);
                                hours = hours?.sort((a, b) => a.day - b.day) ?? [];
                                console.log(kiosk.key);
                                developmentDispatch({
                                    type: DevelopmentDispatchTypes.SetKiosk,
                                    payload: kiosk.key,
                                });

                                dispatch({
                                    type: KioskDispatchTypes.Initialize,
                                    payload: {
                                        kiosk: {
                                            ...kiosk,
                                            localization: generateKioskLocalization(kiosk),
                                        },
                                        dealer: {
                                            ...kiosk.dealer,
                                            localization: generateDealerLocalization(kiosk.dealer),
                                        },
                                        hours: hours,
                                        locale: Locales.en,
                                        bannerImages: bannerImages ?? [],
                                        additionalDealers,
                                        possibleDealers: undefined,
                                        defaultServiceAdvisor,
                                        monochromeTheme: store.get('monochromeTheme') ?? false,
                                        noAnimations: store.get('noAnimations') ?? false,
                                    },
                                });

                                localizationDispatch({
                                    type: LocalizationDispatchTypes.EnableLocale,
                                    payload: kiosk.dealer.primaryLanguage,
                                });
                                kiosk.dealer.alternateLanguage !== Locales.none && localizationDispatch({
                                    type: LocalizationDispatchTypes.EnableLocale,
                                    payload: kiosk.dealer.alternateLanguage,
                                });
                                kiosk.dealer.primaryLanguage !== Locales.en && localizationDispatch({
                                    type: LocalizationDispatchTypes.DisableLocale,
                                    payload: Locales.en,
                                });
                                localizationDispatch({
                                    type: LocalizationDispatchTypes.SetLocale,
                                    payload: kiosk.dealer.primaryLanguage,
                                });
                                setInitFailureCount(0);
                            });
                    }
                    else {
                        throw Error();
                    }
                })
                .catch(async () => {
                    console.log('KioskProvider|Could not load kiosk');

                    if (isElectron()) {
                        await new Promise((resolve) => setTimeout(resolve, 10000));
                        setInitFailureCount(initFailureCount + 1);
                    }
                });
        }
    }, [devKioskKey, developmentDispatch, initFailureCount, isDev, kioskKey, localizationDispatch, machineId, state.kiosk.key, subdomain]);

    useEffect(() => {
        dispatch({
            type: KioskDispatchTypes.SetLocale,
            payload: locale,
        });
    }, [locale]);

    return (
        <KioskStateContext.Provider value={state}>
            <KioskDispatchContext.Provider value={dispatch}>
                <KioskThemeProvider>
                    {initFailureCount === 10 ?
                        <GMDialog
                            ariaLabel={Localization.HeyThereLooksLikeWereHavingSomeTroubleConnecting}
                            open
                            actions={[
                                <GMButton
                                    id={Localization.TryAgain}
                                    title={Localization.TryAgain}
                                    ariaLabel={Localization.TryAgain}
                                    onClick={() => setInitFailureCount(0)}
                                >
                                    {Localization.TryAgain}
                                </GMButton>,
                            ]}
                        >
                            <p>
                                {Localization.HeyThereLooksLikeWereHavingSomeTroubleConnecting}
                            </p>
                            <br />
                            <p>
                                {Localization.GoMotoSupport}
                            </p>
                            <p>
                                {'(800) 961-4151\r'}
                            </p>
                        </GMDialog> : null
                    }
                    {!state.kiosk.key ? <Spinner /> : children}
                </KioskThemeProvider>
            </KioskDispatchContext.Provider>
        </KioskStateContext.Provider>
    );
};

/**
 * Retrieves the full kiosk state from the context
 */
const useKioskFullState: UseKioskFullStateFunction = () => {
    const context = React.useContext(KioskStateContext);
    if (!context?.kiosk) {
        throw new Error('useKioskFullState must be used within a KioskProvider');
    }

    // We know kiosk is not null at this point, but Typescript doesn't.
    return context;
};

/**
 * Retrieves kiosk and dealer from the context
 */
export const useKioskState: UseKioskStateFunction = () => {
    const { kiosk, dealer } = useKioskFullState();

    if (!kiosk || !dealer) {
        throw new Error('useKioskState must be used within a KioskProvider');
    }

    return {
        ...kiosk,
        dealer,
    };
};

/** Returns a dispatch function to update the kiosk */
export const useKioskDispatch: UseKioskDispatchFunction = () => {
    const context = React.useContext(KioskDispatchContext);
    if (context === undefined) {
        throw new Error('useKioskDispatch must be used within a KioskProvider');
    }

    return context;
};

export const useAdditionalDealers: UseAdditionalDealersFunction = () => useKioskFullState().additionalDealers;

export const usePossibleDealers: UsePossibleDealersFunction = () => useKioskFullState().possibleDealers;

export const useDefaultServiceAdvisor: UseDefaultServiceAdvisorFunction = () => useKioskFullState().defaultServiceAdvisor;

export const useMonochromeTheme: UseMonochromeThemeFunction = () => !!(useKioskFullState().monochromeTheme ?? store.get('monochromeTheme'));

export const useNoAnimations: UseNoAnimationsFunction = () => !!(useKioskFullState().noAnimations ?? store.get('noAnimations'));

export const useTowInSupport: UseTowInSupportFunction = () => {
    const {
        dealer: { vehicleTowInOption, hasPhysicalKiosk },
        kioskType,
    } = useKioskState();

    return vehicleTowInOption && (['express', 'outdoor', 'ssk'].includes(kioskType) || (!isElectron() && hasPhysicalKiosk));
};

export const useSwitchDealer: UseSwitchDealerFunction = () => {
    const kioskDispatch = useKioskDispatch();
    const kiosk = useKioskState();
    const additionalDealers = useAdditionalDealers();
    const localizationDispatch = useLocalizationDispatch();

    return (newDealer: Dealer) => {
        if (additionalDealers.some((addl) => addl.key === newDealer.key)) {
            if (kiosk.dealer.primaryLanguage !== newDealer.primaryLanguage) {
                localizationDispatch({
                    type: LocalizationDispatchTypes.EnableLocale,
                    payload: newDealer.primaryLanguage,
                });
                localizationDispatch({
                    type: LocalizationDispatchTypes.DisableLocale,
                    payload: kiosk.dealer.primaryLanguage,
                });
            }
            if (kiosk.dealer.alternateLanguage !== newDealer.alternateLanguage) {
                if (newDealer.alternateLanguage !== Locales.none) {
                    localizationDispatch({
                        type: LocalizationDispatchTypes.EnableLocale,
                        payload: newDealer.alternateLanguage,
                    });
                }
                localizationDispatch({
                    type: LocalizationDispatchTypes.DisableLocale,
                    payload: kiosk.dealer.alternateLanguage,
                });
            }

            localizationDispatch({
                type: LocalizationDispatchTypes.SetLocale,
                payload: newDealer.primaryLanguage,
            });

            kioskDispatch({
                type: KioskDispatchTypes.SetDealer,
                payload: newDealer,
            });
            getDefaultServiceAdvisor(kiosk.key, newDealer.key)
                .then((newSA) => kioskDispatch({
                    type: KioskDispatchTypes.SetDefaultServiceAdvisor,
                    payload: newSA,
                }));
        }
    };
};

// #region Hours
/**
 * This hook provides a variety of functions to calculate timing logic centered around the Hybrid Time Tables.
 *
 * For more information about what this hook provides, click {@link KioskTimeTableHook here} to read more.
 */
export const useKioskTimeTable: KioskTimeTableHook = () => {
    const {
        dealer: { timezone, dmsAppointmentOffsetMinutes },
        hours,
    } = useKioskFullState();
    const { isDev } = useDevelopmentState();

    const kioskTimeTables: IHybridTimeTable[] = hours.filter((day: IHybridTimeTable) => day.startTime !== null && day.endTime !== null);

    const timeFormat: string = 'HH:mm:ss';

    const getKioskOpenHours: GoMotoFunction<[number?], KioskOpenHours> = useCallback((offsetMinutes) => {
        const currentTime: Dayjs = dayjs()
            .tz(Timezone[timezone])
            .add(offsetMinutes ?? 0, 'minute');
        const currentDay: number = currentTime.day();
        const hoursOpenToday: IHybridTimeTable | undefined = kioskTimeTables.find((kioskTimeTable: IHybridTimeTable) => kioskTimeTable.day === currentDay);

        if (!!hoursOpenToday) {
            const startTime: Dayjs = dayjs(hoursOpenToday.startTime, timeFormat)
                .tz(Timezone[timezone], true);
            const endTime: Dayjs = dayjs(hoursOpenToday.endTime, timeFormat)
                .tz(Timezone[timezone], true);
            const isServiceLaneClosed = !(startTime.isBefore(currentTime) && endTime.isAfter(currentTime));

            if (isDev) {
                console.debug(`now${offsetMinutes ? ' (with ' + offsetMinutes + '-minute DMS offset)' : ''}`, currentDay, currentTime.toString());
                console.debug('Open Time: ', startTime.toString());
                console.debug('Closing Time: ', endTime.toString());
                console.debug(`Current ${offsetMinutes ? 'Effective ' : ''}Time: `, currentTime.toString());
                console.debug('serviceLaneClosed', !(startTime.isBefore(currentTime) && endTime.isAfter(currentTime)));
            }

            return {
                currentTime: currentTime,
                currentDay: currentDay,
                startTime: startTime,
                endTime: endTime,
                isServiceLaneClosed: isServiceLaneClosed,
            };
        }
        else {
            return {
                currentTime: currentTime,
                currentDay: currentDay,
                startTime: null,
                endTime: null,
                isServiceLaneClosed: false,
            };
        }
    }, [isDev, kioskTimeTables, timezone]);

    return {
        hours: useMemo(() => kioskTimeTables, [kioskTimeTables]),
        getIsAppointmentTimeAfterOffset: useCallback((appointmentTime) => {
            return dayjs()
                .add(dmsAppointmentOffsetMinutes ?? 0, 'minutes')
                .isAfter(appointmentTime);
        }, [dmsAppointmentOffsetMinutes]),
        getWillKioskBeClosed: useCallback(() => {
            return getKioskOpenHours(dmsAppointmentOffsetMinutes).isServiceLaneClosed;
        }, [dmsAppointmentOffsetMinutes, getKioskOpenHours]),
        getIsKioskActuallyClosed: useCallback(() => {
            return getKioskOpenHours().isServiceLaneClosed;
        }, [getKioskOpenHours]),
        computeNextAvailableCheckinTime: useCallback((useNow) => {
            const { currentTime, currentDay, startTime, endTime, isServiceLaneClosed } = getKioskOpenHours(dmsAppointmentOffsetMinutes);

            if (!isServiceLaneClosed || !kioskTimeTables.length || !!useNow) {
                return dayjs();
            }
            else if (startTime !== null && endTime !== null && currentTime.isBefore(startTime)) {
                isDevelopment() && console.log('next checkin time', startTime.toDate());
                return startTime;
            }
            else {
                const nextAvailableDay: IHybridTimeTable | undefined = kioskTimeTables.find((kioskTimeTable: IHybridTimeTable) => kioskTimeTable.day > currentDay);
                const nextAvailableCheckInTime: Dayjs = dayjs(nextAvailableDay?.startTime ?? kioskTimeTables[0].startTime, timeFormat)
                    .tz(Timezone[timezone], true)
                    .day(nextAvailableDay?.day ?? 7 + kioskTimeTables[0].day);

                isDevelopment() && console.log('next checkin time', nextAvailableCheckInTime.toDate());
                return nextAvailableCheckInTime;
            }
        }, [dmsAppointmentOffsetMinutes, getKioskOpenHours, kioskTimeTables, timezone]),
    };
};
// #endregion

// #region Reset
/**
 * Resets the kiosk to it's full initial state.
 */
export const useFullReset: UseFullResetFunction = (redirect = true, abandoned = true) => {
    const navigate = useNavigate();
    const appointmentDispatch = useAppointmentDispatch();
    const { clearLocale } = useClearLocale();
    const finishSession = useSavedSessionFinish();
    const kioskDispatch = useKioskDispatch();
    const appointment = useAppointmentStateUnsafe();

    return useCallback(async () => {
        if (abandoned && appointment && appointment.key && appointment.key.length > 0 && appointment.status === 'not scheduled') {
            await insertAuditTrail(appointment.key, Events.Check_In_Abandoned);
        }
        finishSession();
        appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
        store.session.remove('appointment');
        redirect && navigate('/');
        kioskDispatch({ type: KioskDispatchTypes.RefetchKiosk });
        clearLocale();
    }, [abandoned, appointment, appointmentDispatch, clearLocale, finishSession, kioskDispatch, navigate, redirect]);
};
// #endregion

// #region Initialize

const useGetKioskRetrievalParams: UseGetKioskRetrievalParamsFunction = () => {
    const [fetchKioskParams, setFetchKioskParams] = React.useState<KioskRetrievalParams>(() => {
        const { location: { hostname, search } } = window;
        let kioskKey: string = '';
        let subdomain: string = '';

        if (!isElectron()) {
            const parsed = qs.parse(search, { ignoreQueryPrefix: true });

            // Check for kiosk key in query string
            if ('kioskkey' in parsed && typeof parsed.kioskkey === 'string') {
                kioskKey = parsed.kioskkey;
            }

            // Check for kiosk key in session storage
            if (!kioskKey && store.session.has('kiosk')) {
                kioskKey = store.session.get('kiosk');
            }

            // Check for the kiosk subdomain
            if (process.env.REACT_APP_DOMAIN && hostname.includes('.' + process.env.REACT_APP_DOMAIN)) {
                subdomain = hostname.replace('.' + process.env.REACT_APP_DOMAIN, '');
            }
        }

        // DEV grab kiosk key from environemnt variable
        if (!kioskKey && !subdomain) {
            kioskKey = process.env.REACT_APP_TEST_KIOSK_ID || '';
        }

        return {
            kioskKey: kioskKey,
            subdomain: subdomain,
            machineId: '',
        };
    });
    const { loading: mzeroLoading, machineId: mzeroMachineId } = useMZeroMachineId();
    const { isLinux, hostname } = useHardwareProviderState();

    useEffect(() => {
        console.log('useGetKioskRetrievalParams|fetchKioskParams:', fetchKioskParams);
    }, [fetchKioskParams]);

    useEffect(() => {
        if (!fetchKioskParams.kioskKey && !fetchKioskParams.subdomain && !fetchKioskParams.machineId) {
            console.log('useGetKioskRetrievalParams|checking for machine id');
            let id: string = '';

            /**
             * If this is the express or VSA linux kiosk, the machine ID is pulled from the
             * hostname rather then mzero
             */
            if (isLinux && hostname) {
                id = hostname;
            }
            else if (!isLinux && !mzeroLoading && mzeroMachineId) {
                id = mzeroMachineId;
            }

            if (id) {
                console.log('useGetKioskRetrievalParams|kioskKey from machineId ', id);
                setFetchKioskParams({
                    ...fetchKioskParams,
                    machineId: id,
                });
            }
        }
    }, [fetchKioskParams, hostname, isLinux, mzeroLoading, mzeroMachineId]);

    return fetchKioskParams;
};

// #endregion

// #region Localization

const generateKioskLocalization: GenerateKioskLocalizationFunction = (kiosk: Kiosk) =>
    new LocalizedStringsFactory({
        [kiosk.dealer.primaryLanguage]: {
            disclaimer: kiosk.disclaimer,
            hybridErrorMessage: kiosk.hybridErrorMessage,
            otherKioskErrorMessage: kiosk.otherKioskErrorMessage,
            location: kiosk.location,
        },
        [kiosk.dealer.alternateLanguage]: {
            disclaimer: kiosk.disclaimerAltLang || kiosk.disclaimer,
            hybridErrorMessage: kiosk.hybridErrorMessageAltLang || kiosk.hybridErrorMessage,
            otherKioskErrorMessage: kiosk.otherKioskErrorMessageAltLang || kiosk.otherKioskErrorMessage,
            location: kiosk.locationAltLang || kiosk.location,
        },
    });

const generateDealerLocalization: GenerateDealerLocalizationFunction = (dealer: Dealer) =>
    new LocalizedStringsFactory({
        [dealer.primaryLanguage]: {
            vehiclePickupExtraText: dealer.vehiclePickupExtraText,
            disclaimerExtra: dealer.disclaimerExtra,
            echoPageReminderText: dealer.echoPageReminderText,
            customUpsellPageText: dealer.customUpsellPageText,
            upsellDisclaimer: dealer.upsellDisclaimer,
            arrivalInstructions: dealer.arrivalInstructions,
            fullKioskInstructions: dealer.fullKioskInstructions,
            consentToSignMessage: dealer.consentToSignMessage,
            declineConsentToSignMessage: dealer.declineConsentToSignMessage,
            workAuthorizationDisclaimer: dealer.workAuthorizationDisclaimer,
            retainOldPartsAdditionalText: dealer.retainOldPartsAdditionalText,
            miscellaneousDisclaimer: dealer.miscellaneousDisclaimer,
        },
        [dealer.alternateLanguage]: {
            vehiclePickupExtraText: dealer.vehiclePickupExtraTextAltLang || dealer.vehiclePickupExtraText,
            disclaimerExtra: dealer.disclaimerExtraAltLang || dealer.disclaimerExtra,
            echoPageReminderText: dealer.echoPageReminderTextAltLang || dealer.echoPageReminderText,
            customUpsellPageText: dealer.customUpsellPageTextAltLang || dealer.customUpsellPageText,
            upsellDisclaimer: dealer.upsellDisclaimerAltLang || dealer.upsellDisclaimer,
            arrivalInstructions: dealer.arrivalInstructionsAltLang || dealer.arrivalInstructions,
            fullKioskInstructions: dealer.fullKioskInstructionsAltLang || dealer.fullKioskInstructions,
            consentToSignMessage: dealer.consentToSignMessageAltLang,
            declineConsentToSignMessage: dealer.declineConsentToSignMessageAltLang,
            workAuthorizationDisclaimer: dealer.workAuthorizationDisclaimerAltLang,
            retainOldPartsAdditionalText: dealer.retainOldPartsAdditionalTextAltLang,
            miscellaneousDisclaimer: dealer.miscellaneousDisclaimerAltLang,
        },
    });

export const useKioskLocalization: UseKioskLocalizationFunction = () => {
    const { kiosk, dealer } = useKioskFullState();

    // Only allow access to keys.
    return {
        kiosk: kiosk.localization,
        dealer: dealer.localization,
    };
};

// #endregion

// #region Images

export const useKioskImage: UseKioskImageFunction = (image: KioskImageType) => {
    const { keyStorageType, kioskType } = useKioskState();

    switch (image) {
        case 'KeyPickUp':
            return kioskType === 'express'
                ? images.kiosk.ExpressKeyDropAndPickUp
                : keyStorageType === 'locker'
                    ? images.kiosk.KeyPickUpCube
                    : images.kiosk.KeyPickUp247;
        case 'KeyDrop':
            return kioskType === 'express'
                ? images.kiosk.ExpressKeyDropAndPickUp
                : kioskType === 'hybrid'
                    ? images.kiosk.KeyDropHybrid
                    : kioskType === 'vsa2' || kioskType === 'vsa3'
                        ? images.kiosk.KeyDropVSA2
                        : keyStorageType === 'locker'
                            ? images.kiosk.KeyDropCube
                            : images.kiosk.KeyDrop247;
        case 'KeyDropWarning':
            return images.kiosk.HandKey;
        case 'PaymentPop':
            return keyStorageType === 'locker' ? images.kiosk.PaymentPopCube : images.kiosk.PaymentPop247;
        case 'ScanQRCode':
            return kioskType === 'express'
                ? images.kiosk.ExpressQrScan
                : kioskType === 'vsa2' || kioskType === 'vsa3' || kioskType === 'vsa4'
                    ? images.kiosk.QRCodeScanVSA
                    : kioskType === 'hybrid'
                        ? images.kiosk.QRCodeScanHybrid
                        : keyStorageType === 'locker'
                            ? images.kiosk.QRCodeScanCube
                            : images.kiosk.QRCodeScan247;
        case 'LicenseScan':
            return kioskType === 'express'
                ? images.kiosk.LicenseScanExpress
                : kioskType === 'vsa2' || kioskType === 'vsa3' || kioskType === 'vsa4'
                    ? images.kiosk.LicenseScanVSA
                    : kioskType === 'hybrid'
                        ? images.kiosk.LicenseScanHybrid
                        : keyStorageType === 'locker'
                            ? images.kiosk.LicenseScanCube
                            : images.kiosk.LicenseScan247;
        case 'LicenseScanBack':
            return images.kiosk.ScanBarCode;
        case 'RetrieveKey':
            return images.kiosk.HandKey;
        case 'PhoneQR':
            return images.kiosk.ScanQRCode;
        case 'Email':
            return images.kiosk.CheckYourEmail;
        case 'Text':
            return images.kiosk.CheckYourTexts;
        case 'Arrow':
            return images.kiosk.Arrow;
    }
};

export const useKioskBannerImages: UseKioskBannerImagesFunction = () => {
    const { bannerImages } = useKioskFullState();

    return bannerImages;
};

// #endregion

// #region Payments

export const useArePaymentsEnabled: UseArePaymentsEnabledFunction = () => {
    const {
        dealer: { onlinePaymentsEnabled, onlinePaymentsActive },
    } = useKioskState();
    const { cardReaderEnabled } = useCardReader();

    return useCallback(
        () =>
            new Promise<boolean>((resolve) => {
                if (onlinePaymentsEnabled) {
                    resolve(onlinePaymentsActive);
                }
                else {
                    cardReaderEnabled.then(resolve);
                }
            }),
        [onlinePaymentsEnabled, cardReaderEnabled, onlinePaymentsActive],
    );
};

// #endregion